/** Settings → Access — admin-only management of collaborator grants (see api/lib/access.ts).
 * Admin searches an Entra person, sets a per-service level (None / Read-only / Manager), saves.
 * The service list comes from /api/me's `services` (grantable scopes). */
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Combobox,
  Dropdown,
  Option,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  MessageBar,
  tokens,
} from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { SERVICE_LABELS } from '../lib/services'
import Lozenge from './Lozenge'
import type { AccessResponse, AccessLevel, PersonDirectoryResponse, UserAccessRow } from '../types'

const SCOPE_LABELS: Record<string, string> = {
  ...SERVICE_LABELS,
  peopleforce: 'PeopleForce',
  github: 'GitHub',
}

const LEVEL_LABEL: Record<AccessLevel, string> = { readonly: 'Read-only', manager: 'Manager' }

function scopeLabel(id: string): string {
  return SCOPE_LABELS[id] ?? id
}

export default function AccessTab() {
  const [data, setData] = useState<AccessResponse | null>(null)
  const [persons, setPersons] = useState<{ email: string; displayName: string }[]>([])
  const [error, setError] = useState<string | null>(null)
  const [editingEmail, setEditingEmail] = useState<string | null>(null)
  const [draft, setDraft] = useState<Record<string, AccessLevel>>({})
  const [query, setQuery] = useState('')
  const [saving, setSaving] = useState(false)

  function load() {
    apiFetchJson<AccessResponse>('/api/access')
      .then(setData)
      .catch(e => setError(String(e)))
  }

  useEffect(load, [])
  useEffect(() => {
    apiFetchJson<PersonDirectoryResponse>('/api/persons')
      .then(res => setPersons(res.persons.map(p => ({ email: p.email, displayName: p.displayName }))))
      .catch(() => {})
  }, [])

  const services = data?.services ?? []
  const filteredPersons = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return persons
    return persons.filter(p => p.displayName.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
  }, [persons, query])

  function startEdit(email: string, grants: Record<string, AccessLevel> = {}) {
    setEditingEmail(email)
    setDraft(grants)
  }

  async function save() {
    if (!editingEmail) return
    setSaving(true)
    setError(null)
    try {
      await apiFetchJson('/api/access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: editingEmail, grants: draft }),
      })
      setEditingEmail(null)
      setDraft({})
      load()
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  async function remove(email: string) {
    setError(null)
    try {
      await apiFetchJson(`/api/access?email=${encodeURIComponent(email)}`, { method: 'DELETE' })
      load()
    } catch (e) {
      setError(String(e))
    }
  }

  if (!data) {
    return error ? <p>Error: {error}</p> : <Spinner label="Loading access..." style={{ marginTop: 24 }} />
  }

  const editingExisting: UserAccessRow | undefined = data.users.find(u => u.email === editingEmail)

  return (
    <div>
      <p style={{ marginTop: 0, color: tokens.colorNeutralForeground3, maxWidth: 640 }}>
        Grant non-admin users access to specific services. <strong>Read-only</strong> can view the
        service's lists and Dashboard card; <strong>Manager</strong> can additionally edit its
        overrides, license types, icon and run sync/CSV import. Admins always have full access and
        can manage this list. Users with no grants see an empty Dashboard.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '16px 0' }}>
        <Combobox
          placeholder="Search an Entra user to add..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onOptionSelect={(_e, d) => {
            if (d.optionValue) startEdit(d.optionValue, {})
          }}
          style={{ minWidth: 320 }}
        >
          {filteredPersons.map(p => (
            <Option key={p.email} value={p.email} text={p.displayName}>
              {p.displayName} ({p.email})
            </Option>
          ))}
        </Combobox>
      </div>

      {error && <MessageBar intent="error" style={{ marginBottom: 12 }}>{error}</MessageBar>}

      {data.users.length === 0 ? (
        <p>No collaborators yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell style={{ width: 320 }}>User</TableHeaderCell>
              <TableHeaderCell>Access</TableHeaderCell>
              <TableHeaderCell style={{ width: 180 }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.users.map(u => (
              <TableRow key={u.email}>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {Object.entries(u.grants).length === 0
                      ? '—'
                      : Object.entries(u.grants).map(([serviceId, level]) => (
                          <Lozenge key={serviceId} color={level === 'manager' ? 'brand' : 'subtle'}>
                            {scopeLabel(serviceId)}: {LEVEL_LABEL[level]}
                          </Lozenge>
                        ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button size="small" onClick={() => startEdit(u.email, { ...u.grants })}>
                      Edit
                    </Button>
                    <Button size="small" onClick={() => remove(u.email)}>
                      Remove
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editingEmail && (
        <div
          style={{
            marginTop: 24,
            padding: 16,
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            borderRadius: tokens.borderRadiusMedium,
            maxWidth: 560,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Access for {editingExisting?.email ?? editingEmail}
          </div>
          {services.map(serviceId => (
            <div key={serviceId} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ width: 140 }}>{scopeLabel(serviceId)}</span>
              <Dropdown
                style={{ minWidth: 160 }}
                value={draft[serviceId] ? LEVEL_LABEL[draft[serviceId]] : 'No access'}
                selectedOptions={[draft[serviceId] ?? 'none']}
                onOptionSelect={(_e, d) => {
                  setDraft(prev => {
                    const next = { ...prev }
                    if (!d.optionValue || d.optionValue === 'none') delete next[serviceId]
                    else next[serviceId] = d.optionValue as AccessLevel
                    return next
                  })
                }}
              >
                <Option value="none">No access</Option>
                <Option value="readonly">Read-only</Option>
                <Option value="manager">Manager</Option>
              </Dropdown>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button appearance="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={() => {
                setEditingEmail(null)
                setDraft({})
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
